(() => {
  const initialized = new WeakSet();
  const setText = (root, selector, value) => {
    const element = root.querySelector(selector);
    if (element) element.textContent = value;
  };
  function displayError(element, message) {
    element.textContent = message;
    element.hidden = false;
    element.focus();
  }
  function responseMessage(data, fallback) {
    if (typeof data?.description === "string") return data.description;
    if (typeof data?.message === "string") return data.message;
    if (typeof data?.errors === "string") return data.errors;
    return fallback;
  }
  async function cartRequest(url, body) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 25000);
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: { Accept: "application/json" },
        body,
        credentials: "same-origin",
        signal: controller.signal,
      });
      const data = await response.json();
      return { response, data };
    } finally {
      clearTimeout(timeout);
    }
  }
  function initialize(scope = document) {
    scope.querySelectorAll("[data-mc-add-form]").forEach((wrapper) => {
      const form = wrapper.closest("form");
      if (!form || initialized.has(form)) return;
      initialized.add(form);
      const product = form.closest("[data-mc-product]");
      const select = form.querySelector("[data-mc-variant]");
      const quantity = form.querySelector('[name="quantity"]');
      const button = form.querySelector("[data-mc-add]");
      const error = form.querySelector("[data-mc-error]");
      const added = form.querySelector("[data-mc-added]");
      let pending = false;
      function updateVariant(updateUrl) {
        const option = select?.selectedOptions[0];
        if (!option) return;
        setText(product, "[data-mc-price]", option.dataset.price);
        const compare = product.querySelector("[data-mc-compare]");
        if (compare) {
          compare.textContent = option.dataset.compare;
          compare.hidden = !option.dataset.compare;
        }
        setText(product, "[data-mc-sku]", option.dataset.sku);
        const heroImage = product.querySelector(".mc-product__image");
        if (heroImage && option.dataset.image) {
          heroImage.src = option.dataset.image;
          heroImage.srcset = option.dataset.imageSrcset || "";
          heroImage.alt = option.dataset.imageAlt || "";
        }
        button.disabled = pending || option.dataset.available !== "true";
        button.textContent = pending
          ? wrapper.dataset.pendingLabel
          : option.dataset.available === "true"
            ? wrapper.dataset.addLabel
            : wrapper.dataset.soldLabel;
        quantity.min = option.dataset.min || "1";
        quantity.step = option.dataset.step || "1";
        if (option.dataset.max) quantity.max = option.dataset.max;
        else quantity.removeAttribute("max");
        quantity.value = quantity.min;
        if (updateUrl) {
          const url = new URL(location.href);
          url.searchParams.set("variant", option.value);
          history.replaceState({}, "", url);
          added.hidden = true;
          error.hidden = true;
        }
      }
      select?.addEventListener("change", () => updateVariant(true));
      updateVariant(false);
      form.addEventListener("submit", async (event) => {
        if (event.defaultPrevented) return;
        event.preventDefault();
        if (pending || !form.checkValidity()) return;
        const body = new FormData(form);
        const inputStates = [select, quantity].filter(Boolean).map((control) => ({
          control,
          disabled: control.disabled,
        }));
        inputStates.forEach(({ control }) => {
          control.disabled = true;
        });
        pending = true;
        button.disabled = true;
        button.textContent = wrapper.dataset.pendingLabel;
        form.setAttribute("aria-busy", "true");
        error.hidden = true;
        added.hidden = true;
        try {
          const action = new URL(form.action, location.origin);
          action.pathname = action.pathname.replace(/\/?$/, ".js");
          const { response, data } = await cartRequest(action.href, body);
          if (!response.ok || data.status >= 400) {
            displayError(
              error,
              responseMessage(data, wrapper.dataset.errorLabel),
            );
          } else {
            added.hidden = false;
            added.focus();
            document.dispatchEvent(
              new CustomEvent("monarch:cart-change", {
                detail: { source: "product" },
              }),
            );
          }
        } catch {
          displayError(error, wrapper.dataset.networkLabel);
        } finally {
          pending = false;
          inputStates.forEach(({ control, disabled }) => {
            control.disabled = disabled;
          });
          form.removeAttribute("aria-busy");
          button.disabled = select
            ? select.selectedOptions[0]?.dataset.available !== "true"
            : false;
          button.textContent = button.disabled
            ? wrapper.dataset.soldLabel
            : wrapper.dataset.addLabel;
        }
      });
    });
    scope.querySelectorAll("[data-mc-native-pending]").forEach((status) => {
      const form = status.closest("form");
      if (!form || initialized.has(form)) return;
      initialized.add(form);
      let activeEvent = null;
      let timer;
      const clear = () => {
        activeEvent = null;
        clearTimeout(timer);
        form.removeAttribute("aria-busy");
        status.hidden = true;
      };
      form.querySelector(".mc-error")?.focus();
      form.addEventListener("submit", (event) => {
        if (event.defaultPrevented) return;
        if (activeEvent && !activeEvent.defaultPrevented) {
          event.preventDefault();
          return;
        }
        activeEvent = event;
        status.hidden = false;
        form.setAttribute("aria-busy", "true");
        clearTimeout(timer);
        timer = setTimeout(clear, 25000);
        queueMicrotask(() => {
          if (activeEvent === event && event.defaultPrevented) clear();
        });
      });
      window.addEventListener("pageshow", clear);
    });
    scope.querySelectorAll("[data-mc-cart-form]").forEach((form) => {
      if (initialized.has(form)) return;
      initialized.add(form);
      let pending = false;
      const error = form.querySelector("[data-mc-cart-error]");
      const status = form.querySelector("[data-mc-cart-status]");
      form.addEventListener("submit", async (event) => {
        if (event.defaultPrevented) return;
        if (pending) {
          event.preventDefault();
          return;
        }
        if (event.submitter?.name === "checkout") return;
        event.preventDefault();
        if (!form.checkValidity()) return;
        pending = true;
        const controls = [...form.querySelectorAll('button[type="submit"]')];
        controls.forEach((button) => {
          button.disabled = true;
        });
        form.setAttribute("aria-busy", "true");
        error.hidden = true;
        status.textContent = form.dataset.pendingLabel;
        status.hidden = false;
        const data = new FormData();
        form.querySelectorAll('[name="updates[]"]').forEach((input) => {
          data.append(`updates[${input.dataset.lineKey}]`, input.value);
        });
        try {
          const action = new URL(form.action, location.origin);
          action.pathname = action.pathname.replace(/\/$/, "") + "/update.js";
          const result = await cartRequest(action.href, data);
          if (!result.response.ok || result.data.status >= 400) {
            displayError(
              error,
              responseMessage(result.data, form.dataset.errorLabel),
            );
          } else {
            location.reload();
          }
        } catch {
          displayError(error, form.dataset.networkLabel);
        } finally {
          pending = false;
          controls.forEach((button) => {
            button.disabled = false;
          });
          form.removeAttribute("aria-busy");
          status.hidden = true;
        }
      });
    });
  }
  initialize();
  document.addEventListener("shopify:section:load", (event) =>
    initialize(event.target),
  );
})();
